<template>
  <v-card class="rounded-2 pa-4">
    <v-row>
      <!-- Left rail: tabs + message list -->
      <v-col cols="12" md="4" lg="3" class="message-rail">
        <v-tabs
          :model-value="selectedTab"
          @update:modelValue="handleChangeTab"
          color="primary"
          density="comfortable"
        >
          <v-tab>
            <span class="text-primary">Unread</span>
            <v-chip
              v-if="messagesCount > 0"
              size="x-small"
              color="accentOrange"
              variant="flat"
              class="ml-2 font-weight-bold"
            >
              {{ messagesCount }}
            </v-chip>
          </v-tab>
          <v-tab>
            <span class="text-primary">Read</span>
          </v-tab>
        </v-tabs>

        <v-list class="message-rail__list mt-2" nav>
          <template
            v-for="(item, i) in displayedMessages"
            :key="item.userMessageCenterMessageId"
          >
            <v-list-item
              :active="
                item.userMessageCenterMessageId ===
                selectedMessage?.userMessageCenterMessageId
              "
              color="primary"
              rounded="lg"
              @click="selectedMessage = item"
            >
              <template v-slot:prepend>
                <v-icon :color="item.isRead ? 'grey' : 'primary'">
                  {{ item.isRead ? 'mdi-email-open-outline' : 'mdi-email' }}
                </v-icon>
              </template>
              <v-list-item-title
                class="text-primary text-subtitle-2"
                :class="
                  item.isRead ? 'font-weight-regular' : 'font-weight-bold'
                "
              >
                {{ item.messageCenterMessageLabel }}
              </v-list-item-title>
              <v-list-item-subtitle
                v-if="item.messageCenterMessageCreateDateTimeOffset"
                class="text-caption"
              >
                {{
                  formatMessageDate(
                    item.messageCenterMessageCreateDateTimeOffset
                  )
                }}
              </v-list-item-subtitle>
            </v-list-item>
            <v-divider
              v-if="i < displayedMessages.length - 1"
              class="mx-2"
            />
          </template>
        </v-list>
      </v-col>

      <!-- Right panel: selected message or empty state -->
      <v-col cols="12" md="8" lg="9">
        <v-alert
          icon="mdi-email-open-outline"
          prominent
          type="info"
          color="primary"
          class="ma-3"
          v-if="
            isReadMessagesListEmptyAndTabIsRead ||
            isUnreadMessagesListEmptyAndTabIsUnread
          "
        >
          {{
            `You have no ${
              isUnreadMessagesListEmptyAndTabIsUnread ? 'Unread' : 'Read'
            } messages.`
          }}
        </v-alert>
        <MessageCenterMessagePanel
          v-else
          @toggle="toggleMessage"
          :selectedMessage="selectedMessage"
        />
      </v-col>
    </v-row>
  </v-card>
</template>
<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

import MessageCenterMessagePanel from '@/Core.Service.Domain/MessageCenter/Users/Components/MessageCenterMessagePanel.vue';

import { dateTimeToUSFormatFactory } from '@/Core.Patterns/Factory';

import { IUserMessage } from '@/Core.Service.Domain/MessageCenter/Users/types';

// Reactive variables<Refs>
const selectedMessage = ref<IUserMessage>({
  isExpired: false,
  isRead: false,
  messageCenterMessageCreateDateTimeOffset: '',
  messageCenterMessageId: 0,
  messageCenterMessageLabel: '',
  messageCenterMessageTypeId: 0,
  messagingCenterMessageValue: '',
  userMessageCenterMessageId: 0,
});
const selectedTab = ref(0);

// Store
const store = useStore();
const messagesCount = computed(
  () => store.getters['MessageCenterUsersStore/getUnreadMessagesCount']
);
const messagesByUserList = computed(
  () => store.getters['MessageCenterUsersStore/getMessagesByUser']
);

// Store actions
const setUnreadMessagesCount = () =>
  store.dispatch('MessageCenterUsersStore/setUnreadMessagesCount');
const getMessagesByUser = () =>
  store.dispatch('MessageCenterUsersStore/setMessagesByUser');

// Computed
const getReadMessagesList = computed(() =>
  messagesByUserList.value.filter((item: { isRead: boolean }) => item.isRead)
);
const getUnreadMessagesList = computed(() =>
  messagesByUserList.value.filter((item: { isRead: boolean }) => !item.isRead)
);
const isReadMessagesListEmptyAndTabIsRead = computed(
  () => getReadMessagesList.value.length === 0 && selectedTab.value === 1
);
const isUnreadMessagesListEmptyAndTabIsUnread = computed(
  () => getUnreadMessagesList.value.length === 0 && selectedTab.value === 0
);

// The rail shows the messages for the active tab (Unread = 0, Read = 1).
const displayedMessages = computed<IUserMessage[]>(() =>
  selectedTab.value === 1
    ? getReadMessagesList.value
    : getUnreadMessagesList.value
);

const formatMessageDate = (value: string) =>
  value
    ? dateTimeToUSFormatFactory(value, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '';

// Lifecycle Hooks
const router = useRouter();
watch(router.currentRoute, (to) => {
  if (to.name === 'MessageCenterMyMessages') {
    handleLoadMessages();
  }
});

// The list can be refreshed from outside this component (the broadcast
// pop-up's "Mark as Read" refetches it). Re-point the panel at the fresh copy
// of the selected message, looked up in the ACTIVE tab's list: a message that
// just changed read state has left this tab, so fall back to the tab's first
// message rather than keep showing one the rail no longer lists.
watch(messagesByUserList, () => {
  const visible = displayedMessages.value;
  const fresh = visible.find(
    (item) =>
      item.userMessageCenterMessageId ===
      selectedMessage.value?.userMessageCenterMessageId
  );
  if (fresh) {
    selectedMessage.value = fresh;
  } else if (visible.length) {
    selectedMessage.value = visible[0];
  }
});

onMounted(async () => {
  await handleLoadMessages();
  if (isUnreadMessagesListEmptyAndTabIsUnread.value) {
    selectedTab.value = 1;
    selectedMessage.value = getReadMessagesList.value[0];
  } else {
    selectedMessage.value = getUnreadMessagesList.value[0];
  }
});

// Methods
const handleLoadMessages = async () => {
  try {
    await getMessagesByUser();
  } catch (error) {
    return Promise.reject(error);
  }
};

const toggleMessage = async () => {
  await handleLoadMessages();
  setUnreadMessagesCount();
};

const handleChangeTab = (value: unknown) => {
  selectedTab.value = value as number;
  // Sync the panel with the newly displayed list so it doesn't keep
  // showing a message from the previous tab.
  if (displayedMessages.value.length) {
    selectedMessage.value = displayedMessages.value[0];
  }
};
</script>
<style lang="scss" scoped>
.v-tab {
  text-transform: none !important;
}

// Keep the rail from growing unbounded with many messages.
.message-rail__list {
  max-height: 60vh;
  overflow-y: auto;
}

// Separate the rail from the message panel on wider screens;
// stacks cleanly (no border) once the columns collapse on mobile.
@media (min-width: 960px) {
  .message-rail {
    border-right: 1px solid
      rgba(var(--v-border-color), var(--v-border-opacity, 0.12));
  }
}
</style>
